import CheckCircle from 'assets/check-circle.svg';
import { ReactComponent as Information } from 'assets/exclamation-circle-fill.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import PasswordWithEye from 'components/PasswordWithEye';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useLoadingMask } from 'contexts/LoadingMaskProvider';
import { useModal } from 'contexts/ModalProvider';
import { useToast } from 'contexts/ToastProvider';
import { SetupCreateDto } from 'dto/setup-create.dto';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Validator from 'utils/validator';

const BindCloudAccount = ({ resetForm, updateUserProfile }: { resetForm: VoidFunction; updateUserProfile: VoidFunction }) => {
  const { t } = useTranslation();
  const { defaultUserEmail, cloudUrl } = useConfig();
  const { logout, postUsersBindCloudAccout } = useAuth();
  const { addToast } = useToast();
  const { addConfirmModal, addCustomModal, closeCustomModal } = useModal();
  const { addLoadingMask } = useLoadingMask();

  const handleCloudRegisterPage = () => {
    window.open(`${cloudUrl}/register`);
  };

  const BindCloudAccountContent = () => {
    const methods = useForm<SetupCreateDto>({ defaultValues: { email: '', password: '' } });

    const onClickCancel = () => {
      resetForm();
      closeCustomModal();
    };

    const onClickConfirmBind = (data: SetupCreateDto) => {
      closeCustomModal();
      addConfirmModal({
        open: true,
        id: 'bindCloudConfirmModal',
        btnOk: t('modal.btnOk'),
        btnCancel: t('modal.btnCancel'),
        type: 'info',
        title: t('modal.titleBindCloudConfirmation'),
        message: (
          <div className="flex flex-col gap-6 text-sm">
            <p>{t('modal.bindCloudAccountConfirmationMessageFront')}</p>
            <p>{t('modal.bindCloudAccountConfirmationMessageBack')}</p>
          </div>
        ),
        onClickOk: () => {
          onSubmitBindCloudAccount(data);
        },
        onClickCancel: () => resetForm(),
      });
    };

    const onSubmitBindCloudAccount = (data: SetupCreateDto) => {
      addLoadingMask({
        show: true,
      });
      postUsersBindCloudAccout({ ...data, portalPort: Number(data.portalPort) })
        .then(() => {
          addToast({
            title: t('toast.bindingSuccessful'),
            message: '',
            color: 'bg-toast-success-50',
            icon: CheckCircle,
            text: 'text-toast-success-800',
            svg: 'toastIcon-success',
          });
          updateUserProfile();
          setTimeout(() => logout(), 2000);
        })
        .catch((error) => {
          let message: string = error.response?.data.message ?? '';
          addToast({
            title: t('toast.bindingFailed'),
            message,
            color: 'bg-toast-error-50',
            icon: ErrorCircle,
            text: 'text-toast-error-800',
            svg: 'toastIcon-error',
          });
        })
        .finally(() => {
          resetForm();
          addLoadingMask({
            show: false,
          });
        });
    };

    return (
      <>
        <Information className="w-10 h-10 inline mr-2 rounded-full fill-blue-400 bg-blue-50 p-3" />
        <h3 className="font-bold text-lg inline">{t('modal.titleBindCloudAccount')}</h3>
        <div className="py-4 pl-12 pr-2">
          <div className="flex flex-col gap-6 text-sm">
            <p>{t('modal.bindCloudAccountMessageFront')}</p>
            <p>
              {t('modal.bindCloudAccountMessageBack')}{' '}
              <span className="btn-link text-[#2230F6] cursor-pointer" onClick={handleCloudRegisterPage}>
                {t('modal.bindCloudAccountMessageBackLink')}
              </span>
            </p>
          </div>
          <FormProvider {...methods}>
            <form className="pt-4 text-xs flex flex-col gap-3" onSubmit={methods.handleSubmit(onClickConfirmBind)}>
              <div className="flex flex-col space-y-1 flex-1 w-full">
                <label>{t('profile.formEmail')}</label>
                <input
                  type="text"
                  className={'input w-full' + (methods.formState.errors.email ? ' input-error' : '')}
                  placeholder={t('portalLogin.emailPlaceholder')}
                  {...methods.register('email', {
                    required: Validator().required,
                    pattern: Validator().email,
                    validate: {
                      defaultUserEamilDuplicate: (v) => v !== defaultUserEmail || `${t('login.defaultUserEamilDuplicate')} ${defaultUserEmail}`,
                    },
                  })}
                />
                {methods.formState.errors.email && <span className="span-error">{methods.formState.errors.email.message}</span>}
              </div>
              <div className="flex flex-col space-y-1 flex-1 w-full">
                <label>{t('login.formPassword')}</label>
                <PasswordWithEye
                  register={{
                    ...methods.register('password', {
                      required: Validator().required,
                      minLength: Validator().minLength(8, 63),
                      maxLength: Validator().maxLength(8, 63),
                      pattern: Validator().userPassword,
                    }),
                  }}
                  errorsName={methods.formState.errors.password}
                  size="w-full"
                  placeholder="*********"
                />
                {methods.formState.errors.password && <span className="span-error">{methods.formState.errors.password.message}</span>}
                <p className="text-xs text-gray-500">{t('profile.bindCloudAccoutPasswordRule')}</p>
              </div>
              <div className="flex space-x-1">
                <div className="flex flex-col space-y-1 flex-auto w-3/4">
                  <label>{t('portalLogin.rontoPortalDomain')}</label>
                  <input
                    type="text"
                    className={'input w-full' + (methods.formState.errors.portalHost ? ' input-error' : '')}
                    placeholder={t('portalLogin.rontoPortalDomainPlaceholder')}
                    {...methods.register('portalHost', {
                      required: Validator().required,
                      pattern: Validator().ipDomain,
                    })}
                  />
                  {methods.formState.errors.portalHost && <span className="span-error">{methods.formState.errors.portalHost.message}</span>}
                </div>
                <div className="flex flex-col space-y-1 flex-auto w-1/4">
                  <label>{t('portalLogin.port')}</label>
                  <input
                    type="text"
                    className={'input w-full' + (methods.formState.errors.portalPort ? ' input-error' : '')}
                    placeholder={'443'}
                    {...methods.register('portalPort', {
                      required: Validator().required,
                    })}
                  />
                  {methods.formState.errors.portalPort && <span className="span-error">{methods.formState.errors.portalPort.message}</span>}
                </div>
              </div>
              <div className="flex flex-col space-y-1 flex-1 w-full">
                <label>{t('portalLogin.siteName')}</label>
                <input
                  type="text"
                  className={'input w-full' + (methods.formState.errors.siteName ? ' input-error' : '')}
                  placeholder={t('portalLogin.siteNamePlaceholder')}
                  {...methods.register('siteName', {
                    required: Validator().required,
                    minLength: Validator().minLength(1, 32),
                    maxLength: Validator().maxLength(1, 32),
                    pattern: Validator().minorName,
                  })}
                />
                {methods.formState.errors.siteName && <span className="span-error px-1">{methods.formState.errors.siteName.message}</span>}
              </div>
              <div className="flex flex-col space-y-1 flex-1 w-full">
                <label>{t('portalLogin.description')}</label>
                <div className="bg-white border rounded-lg border-gray-800 border-opacity-20">
                  <textarea
                    className={'textarea w-full textarea-clear' + (methods.formState.errors.siteDescription ? ' textarea-error' : '')}
                    placeholder={t('portalLogin.descriptionPlaceholder')}
                    {...methods.register('siteDescription')}
                  ></textarea>
                </div>
                {methods.formState.errors.siteDescription && (
                  <span className="span-error px-1">{methods.formState.errors.siteDescription.message}</span>
                )}
              </div>
              <div className="flex space-x-2 justify-end">
                <button type="button" className="btn btn-outline" onClick={onClickCancel}>
                  {t('modal.btnCancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={!methods.formState.isDirty}>
                  {t('modal.btnBind')}
                </button>
              </div>
            </form>
          </FormProvider>
        </div>
      </>
    );
  };

  useEffect(() => {
    addCustomModal({
      open: true,
      content: <BindCloudAccountContent />,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};
export default BindCloudAccount;
